import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Phone, MapPin, Calendar as CalendarIcon, CheckCircle2, Loader2, Sparkles, AlertTriangle } from "lucide-react";

export default function DiscoverySection() {
  const navigate = useNavigate();
  const [type, setType] = useState("phone_15m");
  const [date, setDate] = useState();
  const [slots, setSlots] = useState([]);
  const [pickedSlot, setPickedSlot] = useState("");
  const [nextSlots, setNextSlots] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [alreadyUsed, setAlreadyUsed] = useState(null);

  useEffect(() => {
    if (!date) { setSlots([]); return; }
    const d = new Date(date).toISOString().slice(0, 10);
    api.get(`/availability/slots`, { params: { type, date: d } })
      .then((r) => setSlots(r.data.slots || []))
      .catch(() => setSlots([]));
    setPickedSlot("");
  }, [date, type]);

  // Quick-pick: load the next 6 available slots when type changes
  useEffect(() => {
    api.get(`/availability/next-slots`, { params: { type, limit: 6 } })
      .then((r) => setNextSlots(r.data.slots || []))
      .catch(() => setNextSlots([]));
  }, [type]);

  // Check eligibility when company name + email change
  useEffect(() => {
    if (!form.email || !form.company) { setAlreadyUsed(null); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/discovery/eligibility", { params: { email: form.email, company: form.company } });
        setAlreadyUsed(r.data.already_used ? r.data : null);
      } catch { setAlreadyUsed(null); }
    }, 500);
    return () => clearTimeout(t);
  }, [form.email, form.company]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.name || !form.email || !form.company) return toast.error("Nom, email et entreprise requis");
    if (!date || !pickedSlot) return toast.error("Choisissez une date et un créneau");
    setSubmitting(true);
    try {
      const payload = {
        type, name: form.name, email: form.email, phone: form.phone, company: form.company,
        preferred_date: new Date(date).toISOString().slice(0, 10),
        preferred_time: pickedSlot,
        message: form.message || "RDV découverte (1er RDV gratuit / entreprise)",
        is_discovery: true,
      };
      await api.post("/appointments", payload);
      setSubmitted(true);
      toast.success("RDV envoyé ! Vous recevrez la confirmation par email.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de la réservation");
    } finally { setSubmitting(false); }
  };

  return (
    <section id="decouverte" className="py-20 sm:py-28 bg-gradient-to-b from-[#FAFAFA] to-white" data-testid="discovery-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-xs font-bold tracking-[0.3em] uppercase text-[#06B6D4]">Premier rendez-vous</div>
          <h2 className="display mt-3 text-3xl sm:text-5xl text-[#0A1128] font-extrabold leading-[1.05]">
            Votre 1<sup className="text-3xl">er</sup> RDV découverte est <span className="text-[#06B6D4]">offert.</span>
          </h2>
          <p className="mt-4 text-[#475569] text-lg">
            15 minutes au téléphone ou en physique. 1 RDV gratuit par entreprise — qu'on utilise pour comprendre votre projet et vous orienter.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Booking form */}
          <div className="lg:col-span-3 bg-white border border-black/8 rounded-3xl p-6 shadow-sm">
            {submitted ? (
              <div className="text-center p-8" data-testid="discovery-submitted">
                <CheckCircle2 className="text-green-700 mx-auto" size={48} />
                <div className="display text-2xl font-extrabold text-[#0A1128] mt-3">RDV envoyé !</div>
                <p className="text-sm text-[#475569] mt-2 max-w-md mx-auto">L'équipe BESSON valide votre RDV sous 24h et vous envoie la confirmation par email. À très vite !</p>
                <Button onClick={() => navigate("/portail/connexion")} data-testid="discovery-go-portal" className="mt-6 bg-[#06B6D4] text-[#0A1128] hover:bg-[#67E8F9] font-bold rounded-full px-7 py-6">
                  Créer mon Espace Entreprise
                </Button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="discovery-form" className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><Label>Prénom & Nom *</Label><Input data-testid="disc-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                  <div><Label>Email *</Label><Input data-testid="disc-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                  <div><Label>Téléphone</Label><Input data-testid="disc-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                  <div><Label>Entreprise (ou idée) *</Label><Input data-testid="disc-company" required value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></div>
                </div>

                {alreadyUsed && (
                  <div className="bg-[#FFDCA8]/40 border border-[#FFDCA8] rounded-xl p-3 flex items-start gap-2 text-xs text-[#92400E]" data-testid="discovery-already-used">
                    <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                    <div>
                      <b>Votre RDV découverte gratuit a déjà été utilisé pour "{alreadyUsed.company}".</b><br />
                      Pour un nouveau rendez-vous, accédez à votre <a href="/portail" className="underline font-bold">Espace Entreprise</a> ou réservez une <b>consultation stratégique payante</b>.
                    </div>
                  </div>
                )}

                <div className="flex gap-2 text-xs">
                  <button type="button" onClick={() => setType("phone_15m")} data-testid="disc-type-phone" className={`flex-1 py-2.5 rounded-lg font-bold flex items-center justify-center gap-1.5 ${type === "phone_15m" ? "bg-[#06B6D4] text-[#0A1128]" : "bg-[#FAFAFA] text-[#64748B] hover:bg-black/5"}`}>
                    <Phone size={14} /> Téléphone 15 min
                  </button>
                  <button type="button" onClick={() => setType("physical")} data-testid="disc-type-physical" className={`flex-1 py-2.5 rounded-lg font-bold flex items-center justify-center gap-1.5 ${type === "physical" ? "bg-[#06B6D4] text-[#0A1128]" : "bg-[#FAFAFA] text-[#64748B] hover:bg-black/5"}`}>
                    <MapPin size={14} /> Physique
                  </button>
                </div>

                {/* Quick-pick: next available slots */}
                {nextSlots.length > 0 && (
                  <div className="bg-gradient-to-r from-[#06B6D4]/8 to-[#A7F3D0]/8 border border-[#06B6D4]/20 rounded-2xl p-3" data-testid="disc-next-slots">
                    <div className="text-[10px] font-bold tracking-widest uppercase text-[#06B6D4] mb-2 flex items-center gap-1">
                      <Sparkles size={11} /> Prochains créneaux disponibles
                    </div>
                    <div className="flex gap-1.5 overflow-x-auto pb-1">
                      {nextSlots.map((s) => {
                        const dObj = new Date(s.date);
                        const wd = dObj.toLocaleDateString("fr-FR", { weekday: "short" });
                        const dm = dObj.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
                        const iso = s.date;
                        const isPicked = pickedSlot === s.time && date && new Date(date).toISOString().slice(0, 10) === iso;
                        return (
                          <button
                            type="button"
                            key={`${s.date}-${s.time}`}
                            data-testid={`disc-quick-${s.date}-${s.time}`}
                            onClick={() => {
                              const [y, m, d] = s.date.split("-").map(Number);
                              setDate(new Date(y, m - 1, d));
                              setTimeout(() => setPickedSlot(s.time), 50);
                            }}
                            className={`shrink-0 flex flex-col items-center px-3 py-2 rounded-xl border text-center transition-colors ${
                              isPicked ? "bg-[#06B6D4] text-[#0A1128] border-[#06B6D4]" : "bg-white border-black/10 hover:border-[#06B6D4] text-[#0A1128]"
                            }`}
                          >
                            <span className="text-[10px] uppercase tracking-widest opacity-75">{wd}</span>
                            <span className="text-xs font-extrabold">{dm}</span>
                            <span className="text-[13px] font-extrabold tabular-nums mt-0.5">{s.time}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="bg-[#FAFAFA] rounded-xl p-2">
                    <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().toDateString())} className="text-sm" />
                  </div>
                  <div>
                    {date ? (
                      <>
                        <div className="text-xs text-[#64748B] mb-1.5 flex items-center gap-1"><CalendarIcon size={12} /> Créneaux disponibles</div>
                        {slots.length === 0 && <div className="text-xs text-[#92400E] bg-[#FFDCA8]/40 rounded-lg p-2">Aucun créneau ce jour.</div>}
                        <div className="grid grid-cols-3 gap-1.5">
                          {slots.map((s) => (
                            <button type="button" key={s} data-testid={`disc-slot-${s}`} onClick={() => setPickedSlot(s)} className={`px-2 py-1.5 rounded text-xs font-semibold transition-colors ${pickedSlot === s ? "bg-[#06B6D4] text-[#0A1128]" : "bg-[#FAFAFA] text-[#475569] hover:bg-black/5"}`}>{s}</button>
                          ))}
                        </div>
                      </>
                    ) : (
                      <div className="text-xs text-[#64748B] p-3 bg-[#FAFAFA] rounded-xl h-full flex items-center">Sélectionnez une date pour voir les créneaux disponibles.</div>
                    )}
                  </div>
                </div>

                <Textarea data-testid="disc-message" rows={2} placeholder="Décrivez en 2 phrases votre projet (optionnel)" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />

                <Button type="submit" disabled={submitting || !!alreadyUsed} data-testid="disc-submit" className="w-full bg-[#06B6D4] hover:bg-[#67E8F9] text-[#0A1128] font-extrabold py-6 text-base">
                  {submitting ? <><Loader2 className="animate-spin mr-2" size={16} />Réservation…</> : "Réserver mon RDV découverte"}
                </Button>
                <div className="text-[10px] text-[#64748B] text-center">Confirmation par email après validation par l'équipe.</div>
              </form>
            )}
          </div>

          {/* Side: portal CTA */}
          <div className="lg:col-span-2 bg-[#0A1128] text-white rounded-3xl p-6 relative overflow-hidden flex flex-col" data-testid="discovery-portal-cta">
            <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full opacity-25" style={{ background: "#67E8F9", filter: "blur(80px)" }} />
            <div className="relative flex-1">
              <Sparkles className="text-[#67E8F9]" size={28} />
              <h3 className="display mt-3 text-2xl font-extrabold">Au-delà du 1<sup>er</sup> RDV ?</h3>
              <p className="mt-2 text-white/70 text-sm">Vos prestations, vos accès, vos devis, votre suivi : tout est centralisé dans votre Espace Entreprise. <b className="text-white">Gratuit, sans engagement.</b></p>
              <ul className="mt-4 space-y-2 text-sm">
                {["Une carte par entreprise (vous pouvez en gérer plusieurs)", "Coordonnées + accès aux outils (chiffrés)", "Tout votre historique BESSON en temps réel"].map((it) => (
                  <li key={it} className="flex items-start gap-2 text-white/85"><CheckCircle2 size={14} className="mt-0.5 flex-shrink-0 text-[#A7F3D0]" /><span>{it}</span></li>
                ))}
              </ul>
            </div>
            <a href="/portail/connexion" data-testid="discovery-side-cta" className="mt-5 inline-flex justify-center items-center gap-2 w-full px-5 py-4 rounded-full bg-white text-[#0A1128] hover:bg-white/90 font-extrabold transition-colors">
              Créer mon Espace Entreprise
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
